import { useErrorHandlingMutation } from '@backoffice-frontend/data-fetching'
import {
  Body1,
  Divider,
  MoiaDialog,
  MoiaDialogContent,
  MoiaDialogFormFooter,
  useMoiaErrorNotification,
  useTheme,
} from '@backoffice-frontend/patterns'
import { PersonXIcon } from '@moia-dev/pace-icons'
import { Button, LoadingIndicator } from '@moia-dev/pace-web'
import type { SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdVehicleStreamConfirmManualNoShowMutation } from './BoardingPassengers.hook'

export const ConfirmManualNoShowDialog = ({
  tripId,
  vehicleId,
  isDialogOpen,
  setIsDialogOpen,
  onSuccess,
}: {
  tripId: string
  vehicleId: string
  isDialogOpen: boolean
  setIsDialogOpen: (isOpen: SetStateAction<boolean>) => void
  onSuccess?: () => void
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()

  const [confirmManualNoShow, { loading }] = useErrorHandlingMutation(
    useAdVehicleStreamConfirmManualNoShowMutation,
  )

  const confirmNoShow = async () => {
    const result = await confirmManualNoShow({
      variables: {
        tripId,
        fleetVehicle: vehicleId,
      },
    })
    if (result.data?.manuallyNoShowPassenger?.error) {
      enqueueMoiaErrorNotification(
        result.data.manuallyNoShowPassenger.error.message,
      )
      return
    }
    onSuccess?.()
    setIsDialogOpen(false)
  }

  return (
    <MoiaDialog
      maxWidth="xs"
      open={isDialogOpen}
      onClose={() => setIsDialogOpen(false)}
      header={t('map_confirmNoShow_button')}
    >
      <Divider />
      <MoiaDialogContent>
        <Body1
          css={{
            marginBottom: theme.space.Space3,
          }}
        >
          {t('adFleetMap_dialog_noShowConfirmation_caption')}
        </Body1>
      </MoiaDialogContent>
      <MoiaDialogFormFooter
        trailing={
          <>
            <Button
              label={t('common_cancel_button')}
              variant="secondary"
              onClick={() => setIsDialogOpen(false)}
            />
            <Button
              label={t('map_confirmNoShow_button')}
              variant={'primary'}
              icon={loading ? <LoadingIndicator size={20} /> : <PersonXIcon />}
              onClick={confirmNoShow}
              disabled={loading}
            />
          </>
        }
      />
    </MoiaDialog>
  )
}
